import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
} from "react";
import { Tooltip as ReactTooltip } from "react-tooltip";
import { normalizeResolutions } from "../../helper/common";
import { Button, Checkbox } from "../design";
import "./VerdictPanel.css";

export const LockIcon = () => (
  <svg
    width="16"
    height="16"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <rect x="3" y="11" width="18" height="11" rx="2" />
    <path d="M7 11V7a5 5 0 0 1 10 0v4" />
    <circle cx="12" cy="16" r="1" />
  </svg>
);

export const InfoIcon = () => (
  <svg
    width="16"
    height="16"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <circle cx="12" cy="12" r="10" />
    <path d="M12 8v4" />
    <path d="M12 16h.01" />
  </svg>
);

export const DotsIcon = () => (
  <svg
    width="18"
    height="18"
    viewBox="0 0 18 18"
    fill="none"
    aria-hidden="true"
  >
    <circle cx="3.5" cy="9" r="1.5" fill="currentColor" />
    <circle cx="9" cy="9" r="1.5" fill="currentColor" />
    <circle cx="14.5" cy="9" r="1.5" fill="currentColor" />
  </svg>
);

export const withColon = (title) => {
  if (!title) return "";
  const trimmed = String(title).trim();
  if (/[:：]$/.test(trimmed)) return trimmed;
  return `${trimmed}:`;
};

// Single-line row where the dots toggle only appears when the line is
// actually truncated (overflowing). Shared with ReportOverview candidate cards.
export const VerdictInlineLine = ({
  title,
  subtitle,
  expanded = false,
  onToggle,
  expandLabel = "Expand details",
  collapseLabel = "Show less",
  strikethrough = false,
  hideToggle = false,
  dotsClassName = "verdict-panel__dots",
}) => {
  const lineRef = useRef(null);
  const titleRef = useRef(null);
  const subtitleRef = useRef(null);
  const [isOverflowing, setIsOverflowing] = useState(false);

  const showPreview = !!subtitle && !expanded && !hideToggle;

  const checkOverflow = useCallback(() => {
    const titleEl = titleRef.current;
    const subEl = subtitleRef.current;
    const lineEl = lineRef.current;
    let overflow = false;
    if (titleEl && titleEl.scrollWidth > titleEl.clientWidth + 1)
      overflow = true;
    if (!overflow && subEl && subEl.scrollWidth > subEl.clientWidth + 1)
      overflow = true;
    if (!overflow && lineEl && lineEl.scrollWidth > lineEl.clientWidth + 1)
      overflow = true;
    setIsOverflowing((prev) => (prev === overflow ? prev : overflow));
  }, []);

  useLayoutEffect(() => {
    if (expanded || hideToggle) return;
    checkOverflow();
  });

  useEffect(() => {
    if (expanded || hideToggle) return;
    checkOverflow();
    window.addEventListener("resize", checkOverflow);
    let observer = null;
    if (typeof ResizeObserver !== "undefined" && lineRef.current) {
      observer = new ResizeObserver(checkOverflow);
      observer.observe(lineRef.current);
    }
    let cancelled = false;
    if (document?.fonts?.ready?.then) {
      document.fonts.ready.then(() => {
        if (!cancelled) checkOverflow();
      });
    }
    return () => {
      cancelled = true;
      window.removeEventListener("resize", checkOverflow);
      if (observer) observer.disconnect();
    };
  }, [checkOverflow, expanded, hideToggle, title, subtitle]);

  const showDots =
    !hideToggle && !!subtitle && (expanded ? isOverflowing : isOverflowing);

  return (
    <div ref={lineRef} className="verdict-panel__inline-line">
      <span
        ref={titleRef}
        className={`verdict-panel__row-title verdict-panel__inline-title${
          strikethrough ? " verdict-panel__struck" : ""
        }`}
        style={showPreview ? undefined : { maxWidth: "100%", flex: 1 }}
      >
        {title}
      </span>
      {showPreview ? (
        <span
          ref={subtitleRef}
          className={`verdict-panel__inline-subtitle${
            strikethrough ? " verdict-panel__struck" : ""
          }`}
        >
          {subtitle}
        </span>
      ) : null}
      {showDots ? (
        <Button
          variant="ghost"
          size="sm"
          iconOnly
          className={`${dotsClassName}${expanded ? ` ${dotsClassName}--open` : ""}`}
          style={{ marginLeft: "auto", flexShrink: 0 }}
          onClick={(e) => {
            e.stopPropagation();
            onToggle?.();
          }}
          aria-expanded={expanded}
          aria-label={expanded ? collapseLabel : expandLabel}
          iconStart={
            <span aria-hidden="true" style={{ display: "inline-flex" }}>
              <DotsIcon />
            </span>
          }
        />
      ) : null}
    </div>
  );
};

const VerdictPanel = ({
  _t = (key, fallback) => fallback || key,
  individualWarnings = {},
  individualResolutions = {},
  selectedResolveTenantIds = [],
  toggleResolveTenant,
  formatMissingDoc,
  expandedDocs = {},
  forceExpandDocs = false,
  toggleDocsExpanded,
  hasPendingChanges,
  resolvingDocs,
  resolveActionLabel,
  onResolveClick,
  blockingRows = [],
  guarantorItems = [],
  kycWarnings = {},
  onKycTenantClick,
  isVerdictLoading = false,
}) => {
  const [expandedInline, setExpandedInline] = useState({});

  const warnings =
    individualWarnings && typeof individualWarnings === "object"
      ? individualWarnings
      : {};
  const selectedIds = Array.isArray(selectedResolveTenantIds)
    ? selectedResolveTenantIds
    : [];
  const expandedByTenant =
    expandedDocs && typeof expandedDocs === "object" ? expandedDocs : {};
  const safeBlockingRows = Array.isArray(blockingRows) ? blockingRows : [];
  const safeGuarantorItems = Array.isArray(guarantorItems)
    ? guarantorItems
    : [];
  const hasMissing = Object.keys(warnings).length > 0;
  const kycWarningsSafe =
    kycWarnings && typeof kycWarnings === "object" ? kycWarnings : {};
  const hasKyc = Object.keys(kycWarningsSafe).length > 0;
  const hasToResolve = hasMissing || hasKyc;
  const hasBlocking = safeBlockingRows.length > 0;
  const hasGuarantor = safeGuarantorItems.length > 0;
  const hasCombined = hasBlocking || hasToResolve;

  const toggleInline = (key, tenantIdForDocs) => {
    if (
      tenantIdForDocs &&
      typeof toggleDocsExpanded === "function" &&
      Object.prototype.hasOwnProperty.call(warnings, tenantIdForDocs)
    ) {
      toggleDocsExpanded(tenantIdForDocs);
    }
    setExpandedInline((prev) => ({ ...prev, [key]: !prev[key] }));
  };

  const isInlineExpanded = (key, tenantIdForDocs) => {
    if (forceExpandDocs) return true;
    if (
      tenantIdForDocs &&
      (expandedByTenant[tenantIdForDocs] || expandedInline[key])
    )
      return true;
    return !!expandedInline[key];
  };

  const safeFormatMissingDoc =
    typeof formatMissingDoc === "function" ? formatMissingDoc : (v) => v;

  if (isVerdictLoading) {
    return (
      <>
        <div
          className="verdict-panel__group verdict-panel__group--loading"
          aria-busy="true"
          aria-live="polite"
          style={{ minHeight: 96 }}
        >
          <h3 className="verdict-panel__heading">
            {_t("toResolve", "TO RESOLVE")}
          </h3>
          <p
            className="verdict-panel__subtitle verdict-panel__subtitle--loading"
            style={{
              display: "flex",
              alignItems: "center",
              gap: 8,
              marginBottom: 0,
            }}
          >
            <span className="verdict-panel__spinner" aria-hidden="true" />
            {_t(
              "checkingVerifications",
              "Checking document and verification status…"
            )}
          </p>
        </div>
      </>
    );
  }

  if (!hasMissing && !hasKyc && !hasBlocking && !hasGuarantor) return null;

  const renderInlineLine = ({
    expandKey,
    tenantIdForDocs,
    title,
    subtitle,
    fullSubtitle,
    extraBelow,
    strikethrough,
  }) => {
    const expanded = isInlineExpanded(expandKey, tenantIdForDocs);
    return (
      <div className="verdict-panel__row-content">
        <VerdictInlineLine
          title={title}
          subtitle={subtitle}
          expanded={expanded}
          onToggle={() => toggleInline(expandKey, tenantIdForDocs)}
          expandLabel={_t("expandDetails", "Expand details")}
          collapseLabel={_t("showLess", "Show less")}
          strikethrough={strikethrough}
          hideToggle={forceExpandDocs}
        />
        {subtitle && expanded && !forceExpandDocs ? (
          <div
            className={`verdict-panel__inline-full${
              strikethrough ? " verdict-panel__struck" : ""
            }`}
          >
            {fullSubtitle || subtitle}
          </div>
        ) : null}
        {subtitle && forceExpandDocs ? (
          <div
            className={`verdict-panel__inline-full verdict-panel__inline-full--forced${
              strikethrough ? " verdict-panel__struck" : ""
            }`}
          >
            {fullSubtitle || subtitle}
          </div>
        ) : null}
        {extraBelow}
      </div>
    );
  };

  return (
    <>
      {hasGuarantor && (
        <div className="verdict-panel__group">
          <h3 className="verdict-panel__heading">
            {_t("guarantorRecommended", "Guarantor recommended")}
          </h3>
          <ul
            className="verdict-panel__list verdict-panel__list--inline"
            style={{ marginTop: 6 }}
          >
            {safeGuarantorItems.map((item, index) => {
              const key = `guarantor-${item.id || item.tenantName || index}`;
              return (
                <li key={key} className="verdict-panel__row">
                  <span className="verdict-panel__lock" aria-hidden="true">
                    <LockIcon />
                  </span>
                  {renderInlineLine({
                    expandKey: key,
                    title: withColon(item.tenantName),
                    subtitle: item.reason || "",
                    fullSubtitle: item.reason || "",
                  })}
                </li>
              );
            })}
          </ul>
        </div>
      )}

      {hasCombined && (
        <div className="verdict-panel__group verdict-panel__group--combined">
          {hasPendingChanges && (
            <div className="verdict-panel__group-header no-print">
              <div
                className="report-overview__resolve-actions no-print"
                style={{ margin: 0 }}
              >
                <Button
                  size="sm"
                  className="verdict-panel__resolve-action"
                  onClick={onResolveClick}
                  disabled={resolvingDocs}
                >
                  <span>
                    {resolvingDocs
                      ? `${_t("updating", "Updating")}...`
                      : resolveActionLabel}
                  </span>
                </Button>
              </div>
            </div>
          )}
          <ul className="verdict-panel__list verdict-panel__list--inline">
            {safeBlockingRows.map((row, index) => {
              const key = row.key || `blocking-${row.title}-${index}`;
              const expandKey = `blocking:${key}`;
              return (
                <li key={expandKey} className="verdict-panel__row">
                  <span
                    className="verdict-panel__lock verdict-panel__lock--blocking"
                    aria-hidden="true"
                  >
                    <LockIcon />
                  </span>
                  {renderInlineLine({
                    expandKey,
                    title: withColon(row.title),
                    subtitle: row.desc || "",
                    fullSubtitle: row.desc || "",
                  })}
                </li>
              );
            })}

            {Object.entries(warnings).map(([tenantId, warning]) => {
              const isResolved = !!warning?.isResolved;
              const resolution = normalizeResolutions(
                individualResolutions?.[tenantId]
              )?.[0];
              const isChecked = tenantId && selectedIds.includes(tenantId);
              const docs = warning?.docsMissing || [];
              const docsText = docs.map(safeFormatMissingDoc).join(", ");
              const title = withColon(
                `${warning?.label} ${_t("has", "has")} ${docs.length} ${_t(
                  "missingDocuments",
                  "missing documents"
                )}`
              );
              const expandKey = `missing:${tenantId}`;
              const extraBelow = (
                <>
                  {isResolved && resolution?.landlordName && (
                    <span className="report-overview__resolved-by">
                      {_t("resolvedByLandlord", "Resolved by")}{" "}
                      <strong>{resolution?.landlordName}</strong>
                    </span>
                  )}
                  {isResolved && resolution?.note && (
                    <>
                      <span
                        className="report-overview__resolved-note no-print"
                        data-tooltip-id={`verdict-note-${tenantId}`}
                      >
                        i
                      </span>
                      <ReactTooltip
                        id={`verdict-note-${tenantId}`}
                        place="top"
                        noArrow
                        className="react-tooltip-styling report-overview-resolved-tooltip"
                        render={() => <p>{resolution?.note}</p>}
                      />
                      <span className="pdf-only verdict-panel__resolved-note-text">
                        — {resolution?.note}
                      </span>
                    </>
                  )}
                </>
              );
              return (
                <li key={tenantId} className="verdict-panel__row">
                  <span
                    className="no-print"
                    style={{ marginTop: 2, flexShrink: 0 }}
                  >
                    <Checkbox
                      checked={!!isChecked}
                      disabled={!tenantId}
                      onChange={() => toggleResolveTenant?.(tenantId)}
                    />
                  </span>
                  {renderInlineLine({
                    expandKey,
                    tenantIdForDocs: tenantId,
                    title,
                    subtitle: docsText,
                    fullSubtitle: docsText,
                    extraBelow:
                      isResolved &&
                      (resolution?.landlordName || resolution?.note)
                        ? extraBelow
                        : null,
                    strikethrough: isResolved,
                  })}
                </li>
              );
            })}

            {Object.entries(kycWarningsSafe).map(([tenantId, warning]) => {
              const unresolvedCount =
                warning?.unresolvedCount ?? warning?.unresolved?.length ?? 0;
              const categoryLabel =
                unresolvedCount === 1
                  ? _t("categoryNeedsReview", "category needs review")
                  : _t("categoriesNeedReview", "categories need review");
              const subtitle =
                unresolvedCount > 0
                  ? `${unresolvedCount} ${categoryLabel} — ${_t(
                      "clickToViewKyc",
                      "click to view details"
                    )}`
                  : _t("kycReviewNeeded", "KYC review needed — click to view");
              const title = withColon(
                `${warning?.label} ${_t(
                  "kycRequiresReview",
                  "KYC/AML requires review"
                )}`
              );
              const expandKey = `kyc:${tenantId}`;
              return (
                <li
                  key={`kyc-${tenantId}`}
                  className="verdict-panel__row verdict-panel__row--clickable"
                  role={onKycTenantClick ? "button" : undefined}
                  tabIndex={onKycTenantClick ? 0 : undefined}
                  onClick={() => onKycTenantClick && onKycTenantClick(tenantId)}
                  onKeyDown={(e) => {
                    if (
                      onKycTenantClick &&
                      (e.key === "Enter" || e.key === " ")
                    ) {
                      e.preventDefault();
                      onKycTenantClick(tenantId);
                    }
                  }}
                  style={{
                    cursor: onKycTenantClick ? "pointer" : undefined,
                  }}
                >
                  <span
                    className="verdict-panel__lock"
                    aria-hidden="true"
                    style={{ marginTop: 2, flexShrink: 0 }}
                  >
                    <InfoIcon />
                  </span>
                  {renderInlineLine({
                    expandKey,
                    title: (
                      <span
                        style={{
                          display: "inline-flex",
                          alignItems: "center",
                          gap: 6,
                        }}
                      >
                        <span>{title}</span>
                        <img
                          src="/images/external-link.svg"
                          alt=""
                          className="no-print"
                          style={{
                            width: 14,
                            height: 14,
                            flexShrink: 0,
                          }}
                        />
                      </span>
                    ),
                    subtitle,
                    fullSubtitle: subtitle,
                  })}
                </li>
              );
            })}
          </ul>
        </div>
      )}
    </>
  );
};

export default VerdictPanel;
